import classNames from 'clsx';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { interval } from 'rxjs';

import { TimedStatus } from '@swf/casino';
import StarsButton from '@swf/components/StarsButton/StarsButton';
import Translation from '@swf/components/Translation/Translation';
import { GADimension, Size } from '@swf/core/enums';
import useCasinoBonusGame from '@swf/core/hooks/useCasinoBonusGame';
import useEmbeddedAPI from '@swf/core/hooks/useEmbeddedAPI';
import useScreenSize from '@swf/core/hooks/useScreenSize';
import useTrackers from '@swf/core/hooks/useTrackers';
import { select } from '@swf/core/store';
import type { SpinOfDayData } from '@swf/core/types/CasinoBonus';
import { BonusStatus } from '@swf/core/types/CasinoBonus';
import { Category } from '@swf/core/types/StarsCoinReward';

import Expiry from '../../assets/expiry.svg';
import Container from '../Container/Container';
import type WithClose from '../WithClose';

import {
  TXTCLI_Play,
  TXTWCL_1_Spin,
  TXTWCL_Expires_inX,
  TXTWCL_N0_Spins
} from './SpinOfTheDay.messages.json';
import styles from './SpinOfTheDay.scss';

interface Props extends WithClose {
  data?: SpinOfDayData;
}

const SpinOfTheDay: React.FC<Props> = ({ data, close }) => {
  const isMobile = useSelector(select.device.isMobile);
  const { greaterThan } = useScreenSize();
  const useButton = !isMobile && greaterThan(Size.Small);
  const launchGame = useCasinoBonusGame();
  const { buttonPressed } = useTrackers();

  const trackingDimensions = data && {
    [GADimension.TabName]: 'Spin of the Day',
    [GADimension.CampaignID]: data?.bonusCampaignId,
    [GADimension.TicketID]: data?.bonusId
  };

  const [startTime, setStartTime] = useState<number>(Date.now());

  useEmbeddedAPI();
  const launch = () => {
    if (data) {
      launchGame(data);
      isMobile && buttonPressed({ buttonName: 'Play' }, trackingDimensions);
    }
  };

  useEffect(() => {
    const base = interval(1000).subscribe(() => setStartTime(Date.now()));
    return () => base.unsubscribe();
  }, []);

  return data && data.numberOfTickets > 0 && data.numberOfSpins > 0 ? (
    <Container
      category={Category.SpinOfDay}
      className={classNames(styles.container, { [styles.mobile]: !useButton })}
      close={close}
    >
      <div className={styles.content} onClick={!useButton ? launch : undefined}>
        <div className={styles.iconTile} />
        <div className={styles.ticketInfo}>
          <Translation
            {...(data.numberOfSpins > 1 ? TXTWCL_N0_Spins : TXTWCL_1_Spin)}
            values={{ 0: data.numberOfSpins }}
          />
        </div>
        <div className={classNames(styles.info)}>
          <div className={styles.timeInfoTitle}>
            <Translation {...TXTWCL_Expires_inX} />
          </div>
          <div className={styles.timeIcon}>
            <Expiry />
            <span className={styles.timeInfoContent}>
              <TimedStatus
                running
                startTime={startTime}
                endTime={data.expiryTime}
                count={startTime}
              />
            </span>
          </div>
        </div>
        {useButton && (
          <StarsButton
            onClick={launch}
            disabled={data.status !== BonusStatus.Available}
            translation={TXTCLI_Play}
            className={styles.play}
            trackingDimensions={trackingDimensions}
          />
        )}
      </div>
    </Container>
  ) : null;
};

export default SpinOfTheDay;
